/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import { CHIP_GREEN, CHIP_AMBER, CHIP_GREY } from 'src/components/actionCards/actionCardColors';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Avatar,
    Chip,
    alpha,
    Button,
    Tooltip,
} from '@mui/material';
import { Iconify } from 'src/components';
import { PATH_DASHBOARD } from 'src/routes/paths';
import DemoBlur from 'src/components/DemoBlur';

const RANK_STYLES = {
    1: { emoji: '\u{1F947}', bg: '#fef3c7', text: '#d97706', border: '#fcd34d' },
    2: { emoji: '\u{1F948}', bg: '#f1f5f9', text: '#64748b', border: '#cbd5e1' },
    3: { emoji: '\u{1F949}', bg: '#fed7aa', text: '#c2410c', border: '#fdba74' },
    default: { emoji: '', bg: '#f1f5f9', text: '#94a3b8', border: '#e2e8f0' },
};

export default function TopTeamMembersCards({ practitioners, onPractitionerClick }) {
    const navigate = useNavigate();

    const topFive = practitioners.slice(0, 5);

    const getInitials = (name) => {
        if (!name) return '?';
        return name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    const formatCurrency = (value) => {
        if (value >= 1000000) return `\u00A3${(value / 1000000).toFixed(1)}M`;
        if (value >= 1000) return `\u00A3${(value / 1000).toFixed(1)}K`;
        return `\u00A3${value}`;
    };

    const getRankStyle = (rank) => RANK_STYLES[rank] || RANK_STYLES.default;

    const getConvRateColor = (rate) => {
        const numRate = parseFloat(rate) || 0;
        // Green / amber / GREY — never red (red is reserved for overdue/late).
        if (numRate >= 50) return CHIP_GREEN.text;
        if (numRate >= 30) return CHIP_AMBER.text;
        return CHIP_GREY.text;
    };

    return (
        <Box
            sx={{
                bgcolor: '#fff',
                borderRadius: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                overflow: 'hidden',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    p: 2.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Tooltip
                    title="Top performing dentists ranked by consultation-to-treatment conversion rate. Shows who converts attended consultations into started treatments most effectively."
                    placement="bottom"
                    arrow
                    slotProps={{ tooltip: { sx: { bgcolor: '#1e293b', fontSize: '12px', maxWidth: 320 } } }}
                >
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                        <Typography sx={{ fontSize: '20px', fontWeight: 700 }}>
                            Top Practitioners
                        </Typography>
                        <Iconify icon="mdi:information-outline" width={14} sx={{ color: '#94a3b8' }} />
                    </Box>
                </Tooltip>
                <Button
                    size="small"
                    endIcon={<Iconify icon="eva:arrow-forward-outline" width={16} />}
                    onClick={() => navigate(PATH_DASHBOARD.analyticsReports.byPractitioner)}
                    sx={{
                        color: '#6366f1',
                        fontWeight: 600,
                        fontSize: '13px',
                        textTransform: 'none',
                        '&:hover': { bgcolor: alpha('#6366f1', 0.08) },
                    }}
                >
                    View All
                </Button>
            </Box>

            {/* Team Member Cards */}
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {topFive.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography sx={{ color: '#64748b', fontSize: '14px' }}>
                            No team member data available
                        </Typography>
                    </Box>
                )}
                {topFive.map((member, idx) => {
                    const rank = member.rank || idx + 1;
                    const rankStyle = getRankStyle(rank);
                    const convRate = parseFloat(member.conversionRate) || 0;
                    const convRateColor = getConvRateColor(convRate);

                    return (
                        <Box
                            key={member.id || member.name}
                            onClick={() => onPractitionerClick?.(member)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                p: 2,
                                borderRadius: '12px',
                                border: '1px solid #f1f5f9',
                                cursor: onPractitionerClick ? 'pointer' : 'default',
                                transition: 'all 0.15s ease',
                                '&:hover': onPractitionerClick ? {
                                    bgcolor: '#f8fafc',
                                    borderColor: '#e2e8f0',
                                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                                } : {},
                            }}
                        >
                            {/* Rank Badge */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 32,
                                    height: 32,
                                    borderRadius: '8px',
                                    fontWeight: 700,
                                    fontSize: rankStyle.emoji ? '16px' : '14px',
                                    bgcolor: rankStyle.bg,
                                    color: rankStyle.text,
                                    border: `1px solid ${rankStyle.border}`,
                                    flexShrink: 0,
                                }}
                            >
                                {rankStyle.emoji || rank}
                            </Box>

                            {/* Avatar + Name + Specialty */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                                <DemoBlur>
                                <Avatar
                                    src={member.avatar}
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        bgcolor: '#e2e8f0',
                                        color: '#64748b',
                                        fontWeight: 600,
                                        fontSize: '13px',
                                        flexShrink: 0,
                                    }}
                                >
                                    {getInitials(member.name)}
                                </Avatar>
                                </DemoBlur>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                        sx={{
                                            fontWeight: 500,
                                            fontSize: '14px',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        <DemoBlur>{member.name}</DemoBlur>
                                    </Typography>
                                    {member.specialty && (
                                        <Typography
                                            sx={{
                                                fontSize: '11px',
                                                color: '#94a3b8',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {member.specialty}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>

                            {/* Stats */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    flexShrink: 0,
                                    flexWrap: 'wrap',
                                    justifyContent: 'flex-end',
                                }}
                            >
                                <Chip
                                    label={`${convRate}%`}
                                    size="small"
                                    sx={{
                                        bgcolor: alpha(convRateColor, 0.1),
                                        color: convRateColor,
                                        fontWeight: 600,
                                        fontSize: '11px',
                                        height: 24,
                                    }}
                                />
                                <Chip
                                    label={`${member.consultations || 0} consults`}
                                    size="small"
                                    sx={{
                                        bgcolor: alpha('#8b5cf6', 0.1),
                                        color: '#8b5cf6',
                                        fontWeight: 600,
                                        fontSize: '11px',
                                        height: 24,
                                    }}
                                />
                                <Chip
                                    label={`avg ${formatCurrency(member.avgRevenue || 0)}`}
                                    size="small"
                                    sx={{
                                        bgcolor: alpha('#6366f1', 0.1),
                                        color: '#6366f1',
                                        fontWeight: 600,
                                        fontSize: '11px',
                                        height: 24,
                                    }}
                                />
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

TopTeamMembersCards.propTypes = {
    practitioners: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            name: PropTypes.string,
            avatar: PropTypes.string,
            rank: PropTypes.number,
            specialty: PropTypes.string,
            conversionRate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            consultations: PropTypes.number,
            avgRevenue: PropTypes.number,
        })
    ),
    onPractitionerClick: PropTypes.func,
};

TopTeamMembersCards.defaultProps = {
    practitioners: [],
    onPractitionerClick: null,
};
